import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import {
  UserCheck,
  Clock,
  Wrench,
  AlertTriangle,
  XCircle,
  Loader2,
  CheckCircle,
  CalendarOff,
  Trash2
} from "lucide-react";
import { toast } from "sonner";
import { format } from "date-fns";
import { fmtTz } from "../lib/time";

export default function AdminApprovals() {
  const { authAxios } = useAuth();
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState({
    employee_registrations: [],
    time_off_requests: [],
    service_requests: [],
    failed_inspections: [],
    out_of_service: []
  });
  const [processingId, setProcessingId] = useState(null);

  const fetchApprovals = useCallback(async () => {
    try {
      const res = await authAxios.get('/approvals/all');
      setData(res.data);
    } catch (error) {
      toast.error('Failed to load pending approvals');
    } finally {
      setLoading(false);
    }
  }, [authAxios]);

  useEffect(() => {
    fetchApprovals();
  }, [fetchApprovals]);

  const handleApproveEmployee = async (userId) => {
    setProcessingId(userId);
    try {
      await authAxios.post(`/pending-approvals/${userId}/action`, { action: 'approve' });
      toast.success('Employee approved');
      fetchApprovals();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to approve');
    } finally {
      setProcessingId(null);
    }
  };

  const handleRejectEmployee = async (userId) => {
    if (!window.confirm('Are you sure you want to reject this registration?')) return;
    setProcessingId(userId);
    try {
      await authAxios.post(`/pending-approvals/${userId}/action`, { action: 'reject', reason: 'Rejected by admin' });
      toast.success('Employee rejected');
      fetchApprovals();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to reject');
    } finally {
      setProcessingId(null);
    }
  };

  const handleDeletePendingUser = async (userId, name) => {
    if (!window.confirm(`Permanently delete ${name} from pending registrations?`)) return;
    setProcessingId(userId);
    try {
      await authAxios.delete(`/pending-approvals/${userId}`);
      toast.success('Pending registration deleted');
      fetchApprovals();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete');
    } finally {
      setProcessingId(null);
    }
  };

  const handleTimeOffAction = async (requestId, action) => {
    setProcessingId(requestId);
    try {
      await authAxios.post(`/time-off/${requestId}/action`, { action });
      toast.success(`Request ${action}d`);
      fetchApprovals();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to process');
    } finally {
      setProcessingId(null);
    }
  };

  const handleDeleteTimeOff = async (requestId) => {
    if (!window.confirm('Delete this time off request?')) return;
    setProcessingId(requestId);
    try {
      await authAxios.delete(`/time-off/${requestId}`);
      toast.success('Time off request deleted');
      fetchApprovals();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete');
    } finally {
      setProcessingId(null);
    }
  };

  const handleServiceRequestAction = async (requestId, action) => {
    setProcessingId(requestId);
    try {
      await authAxios.post(`/forms/service-requests/${requestId}/action?action=${action}`);
      toast.success(`Service request ${action}`);
      fetchApprovals();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to process');
    } finally {
      setProcessingId(null);
    }
  };

  const handleDeleteServiceRequest = async (requestId) => {
    if (!window.confirm('Delete this service request?')) return;
    setProcessingId(requestId);
    try {
      await authAxios.delete(`/forms/service-requests/${requestId}`);
      toast.success('Service request deleted');
      fetchApprovals();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete');
    } finally {
      setProcessingId(null);
    }
  };

  const handleReturnToService = async (oosId) => {
    setProcessingId(oosId);
    try {
      await authAxios.post(`/equipment/out-of-service/${oosId}/return`);
      toast.success('Equipment returned to service');
      fetchApprovals();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to process');
    } finally {
      setProcessingId(null);
    }
  };

  const handleDeleteOutOfService = async (oosId) => {
    if (!window.confirm('Delete this out-of-service record?')) return;
    setProcessingId(oosId);
    try {
      await authAxios.delete(`/equipment/out-of-service/${oosId}`);
      toast.success('Record deleted');
      fetchApprovals();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete');
    } finally {
      setProcessingId(null);
    }
  };

  const handleAcknowledgeInspection = async (inspectionId) => {
    setProcessingId(inspectionId);
    try {
      await authAxios.post(`/equipment/inspections/${inspectionId}/approve`);
      toast.success('Inspection acknowledged');
      fetchApprovals();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to process');
    } finally {
      setProcessingId(null);
    }
  };

  const handleDeleteInspection = async (inspectionId) => {
    if (!window.confirm('Delete this inspection record?')) return;
    setProcessingId(inspectionId);
    try {
      await authAxios.delete(`/equipment/inspections/${inspectionId}`);
      toast.success('Inspection deleted');
      fetchApprovals();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete');
    } finally {
      setProcessingId(null);
    }
  };

  const totalCount = 
    data.employee_registrations.length +
    data.time_off_requests.length +
    data.service_requests.length +
    data.failed_inspections.length +
    data.out_of_service.length;

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    try { return fmtTz(dateStr, 'MMM d, yyyy h:mm a'); } catch { return dateStr; }
  };

  const disabled = (id) => processingId === id;

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8" data-testid="admin-approvals">
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-stone-900 font-heading">Pending Approvals</h1>
        <p className="text-stone-500 mt-1">Review and process all items requiring admin approval</p>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="w-8 h-8 animate-spin text-emerald-600" />
        </div>
      ) : totalCount === 0 ? (
        <Card className="border-0 shadow-lg">
          <CardContent className="py-16 text-center">
            <div className="w-16 h-16 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <CheckCircle className="w-8 h-8 text-emerald-600" />
            </div>
            <h3 className="text-lg font-semibold text-stone-900 mb-2">All Caught Up!</h3>
            <p className="text-stone-500">No items pending approval</p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-6">
          {/* Employee Registrations */}
          {data.employee_registrations.length > 0 && (
            <Card className="border-0 shadow-lg">
              <CardHeader>
                <CardTitle className="text-lg font-heading flex items-center gap-2">
                  <UserCheck className="w-5 h-5 text-emerald-600" />
                  Employee Registrations ({data.employee_registrations.length})
                </CardTitle>
                <CardDescription>New employees awaiting account approval</CardDescription>
              </CardHeader>
              <CardContent>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Name</TableHead>
                      <TableHead>Email</TableHead>
                      <TableHead>Department</TableHead>
                      <TableHead>Registered</TableHead>
                      <TableHead className="text-right">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {data.employee_registrations.map((emp) => (
                      <TableRow key={emp.id}>
                        <TableCell className="font-medium">{emp.first_name} {emp.last_name}</TableCell>
                        <TableCell>{emp.email}</TableCell>
                        <TableCell>{emp.department}</TableCell>
                        <TableCell>{formatDate(emp.registered_at)}</TableCell>
                        <TableCell className="text-right">
                          <div className="flex items-center justify-end gap-1">
                            <Button size="sm" onClick={() => handleApproveEmployee(emp.id)} disabled={disabled(emp.id)} className="bg-emerald-600 hover:bg-emerald-500" data-testid={`approve-emp-${emp.id}`}>
                              {disabled(emp.id) ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Approve'}
                            </Button>
                            <Button size="sm" variant="outline" onClick={() => handleRejectEmployee(emp.id)} disabled={disabled(emp.id)} className="text-rose-600 border-rose-200 hover:bg-rose-50" data-testid={`reject-emp-${emp.id}`}>
                              Reject
                            </Button>
                            <Button size="icon" variant="ghost" onClick={() => handleDeletePendingUser(emp.id, `${emp.first_name} ${emp.last_name}`)} disabled={disabled(emp.id)} data-testid={`delete-emp-${emp.id}`}>
                              <Trash2 className="w-4 h-4 text-rose-500" />
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          )}

          {/* Time Off Requests */}
          {data.time_off_requests.length > 0 && (
            <Card className="border-0 shadow-lg">
              <CardHeader>
                <CardTitle className="text-lg font-heading flex items-center gap-2">
                  <CalendarOff className="w-5 h-5 text-blue-600" />
                  Time Off Requests ({data.time_off_requests.length})
                </CardTitle>
              </CardHeader>
              <CardContent>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Employee</TableHead>
                      <TableHead>Type</TableHead>
                      <TableHead>Dates</TableHead>
                      <TableHead>Days</TableHead>
                      <TableHead className="text-right">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {data.time_off_requests.map((req) => (
                      <TableRow key={req.id}>
                        <TableCell className="font-medium">{req.employee_name}</TableCell>
                        <TableCell><Badge variant="outline">{req.request_type}</Badge></TableCell>
                        <TableCell>{fmtTz(req.start_date, 'MMM d')} - {fmtTz(req.end_date, 'MMM d, yyyy')}</TableCell>
                        <TableCell>{req.total_days}</TableCell>
                        <TableCell className="text-right">
                          <div className="flex items-center justify-end gap-1">
                            <Button size="sm" variant="outline" onClick={() => handleTimeOffAction(req.id, 'approve')} disabled={disabled(req.id)} className="text-emerald-600 border-emerald-200 hover:bg-emerald-50">Approve</Button>
                            <Button size="sm" variant="outline" onClick={() => handleTimeOffAction(req.id, 'deny')} disabled={disabled(req.id)} className="text-rose-600 border-rose-200 hover:bg-rose-50">Deny</Button>
                            <Button size="icon" variant="ghost" onClick={() => handleDeleteTimeOff(req.id)} disabled={disabled(req.id)}><Trash2 className="w-4 h-4 text-rose-500" /></Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          )}

          {/* Service Requests */}
          {data.service_requests.length > 0 && (
            <Card className="border-0 shadow-lg">
              <CardHeader>
                <CardTitle className="text-lg font-heading flex items-center gap-2">
                  <Wrench className="w-5 h-5 text-orange-600" />
                  Critical Service Requests ({data.service_requests.length})
                </CardTitle>
              </CardHeader>
              <CardContent>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Equipment</TableHead>
                      <TableHead>Urgency</TableHead>
                      <TableHead>Description</TableHead>
                      <TableHead>Requested By</TableHead>
                      <TableHead className="text-right">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {data.service_requests.map((req) => (
                      <TableRow key={req.id}>
                        <TableCell className="font-medium">{req.equipment_name}</TableCell>
                        <TableCell>
                          <Badge variant="outline" className={req.urgency === 'critical' ? 'bg-rose-100 text-rose-700' : 'bg-orange-100 text-orange-700'}>{req.urgency}</Badge>
                        </TableCell>
                        <TableCell className="max-w-[200px] truncate">{req.description}</TableCell>
                        <TableCell>{req.requested_by_name}</TableCell>
                        <TableCell className="text-right">
                          <div className="flex items-center justify-end gap-1">
                            <Button size="sm" onClick={() => handleServiceRequestAction(req.id, 'approved')} disabled={disabled(req.id)} className="bg-emerald-600 hover:bg-emerald-500">Approve</Button>
                            <Button size="sm" variant="outline" onClick={() => handleServiceRequestAction(req.id, 'denied')} disabled={disabled(req.id)} className="text-rose-600 border-rose-200 hover:bg-rose-50">Deny</Button>
                            <Button size="icon" variant="ghost" onClick={() => handleDeleteServiceRequest(req.id)} disabled={disabled(req.id)}><Trash2 className="w-4 h-4 text-rose-500" /></Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          )}

          {/* Failed Inspections */}
          {data.failed_inspections.length > 0 && (
            <Card className="border-0 shadow-lg">
              <CardHeader>
                <CardTitle className="text-lg font-heading flex items-center gap-2">
                  <AlertTriangle className="w-5 h-5 text-rose-600" />
                  Failed Inspections ({data.failed_inspections.length})
                </CardTitle>
              </CardHeader>
              <CardContent>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Equipment/Vehicle</TableHead>
                      <TableHead>Type</TableHead>
                      <TableHead>Inspector</TableHead>
                      <TableHead>Date</TableHead>
                      <TableHead className="text-right">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {data.failed_inspections.map((insp) => (
                      <TableRow key={insp.id}>
                        <TableCell className="font-medium">{insp.equipment_name || insp.vehicle_name}</TableCell>
                        <TableCell><Badge variant="outline">{insp.inspection_type?.replace('_', ' ')}</Badge></TableCell>
                        <TableCell>{insp.inspector_name}</TableCell>
                        <TableCell>{formatDate(insp.created_at)}</TableCell>
                        <TableCell className="text-right">
                          <div className="flex items-center justify-end gap-1">
                            <Button size="sm" variant="outline" onClick={() => handleAcknowledgeInspection(insp.id)} disabled={disabled(insp.id)}>Acknowledge</Button>
                            <Button size="icon" variant="ghost" onClick={() => handleDeleteInspection(insp.id)} disabled={disabled(insp.id)}><Trash2 className="w-4 h-4 text-rose-500" /></Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          )}

          {/* Out of Service */}
          {data.out_of_service.length > 0 && (
            <Card className="border-0 shadow-lg">
              <CardHeader>
                <CardTitle className="text-lg font-heading flex items-center gap-2">
                  <XCircle className="w-5 h-5 text-stone-600" />
                  Out of Service ({data.out_of_service.length})
                </CardTitle>
              </CardHeader>
              <CardContent>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Equipment</TableHead>
                      <TableHead>Reason</TableHead>
                      <TableHead>Reported By</TableHead>
                      <TableHead>Date</TableHead>
                      <TableHead className="text-right">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {data.out_of_service.map((oos) => (
                      <TableRow key={oos.id}>
                        <TableCell className="font-medium">{oos.equipment_name}</TableCell>
                        <TableCell><Badge variant="outline">{oos.reason?.replace('_', ' ')}</Badge></TableCell>
                        <TableCell>{oos.reported_by_name}</TableCell>
                        <TableCell>{formatDate(oos.created_at)}</TableCell>
                        <TableCell className="text-right">
                          <div className="flex items-center justify-end gap-1">
                            <Button size="sm" onClick={() => handleReturnToService(oos.id)} disabled={disabled(oos.id)} className="bg-emerald-600 hover:bg-emerald-500">Return to Service</Button>
                            <Button size="icon" variant="ghost" onClick={() => handleDeleteOutOfService(oos.id)} disabled={disabled(oos.id)}><Trash2 className="w-4 h-4 text-rose-500" /></Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          )}
        </div>
      )}
    </div>
  );
}
